import React from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';

import { IconButton } from '@eb/eds-icon-button';
import { Icon } from '@eb/eds-icon';
import { MagnifyingGlassChunky as MagnifyingGlassChunkySvg } from '@eb/eds-iconography';
import { PlusChunky as PlusChunkySvg } from '@eb/eds-iconography';
import { getNarrowMenuInfo } from '@eb/eds-global-header';

import { setWindowLocation } from '@eb/http';

import {
    getCreateEventCTA,
    getHelpMenu,
    getLogoInfo,
    getUserMenuInfo,
    getNonUserQuickLinks,
    getOrganizeMenu,
    getNarrowMenuLinks,
    FALLBACK_SUGGESTIONS,
    ENV_SHAPE_PROP_TYPE,
    REQUEST_SHAPE_PROP_TYPE,
    USER_SHAPE_PROP_TYPE,
} from '@eb/site-header';

import gettext from '@eb/gettext';
// import MediaQuery from '../MediaQuery';
import GlobalHeaderLogo from './GlobalHeaderLogo';
import GlobalHeaderSearch, { getSearchInfo } from './GlobalHeaderSearch';
import {
    GlobalHeaderQuickLinks,
    GlobalHeaderCallToAction,
    GlobalHeaderMenu,
    GlobalHeaderMenus,
} from './GlobalHeaderNavigationBar';
import {
    getLikesQuickLinks,
    getMyTicketsQuickLinks,
    getBugQuickLinks,
} from './core-js-global-copies';

import { FEATURE_FLAGS_PROPTYPES } from '../../constants/propTypes';

import './ConsumerHeaderIndex.scss';

class ConsumerHeader extends React.Component {
    static propTypes = {
        env: ENV_SHAPE_PROP_TYPE.isRequired,
        request: REQUEST_SHAPE_PROP_TYPE.isRequired,
        user: USER_SHAPE_PROP_TYPE.isRequired,
        onSearchSelect: PropTypes.func,
        onAdditionalMenuTextItemSelect: PropTypes.func,
        handleSearch: PropTypes.func,
        onSearchFocus: PropTypes.func,
        isHomePage: PropTypes.bool,
        isSearchPage: PropTypes.bool,
        isMobile: PropTypes.bool,
        id: PropTypes.string,
        featureFlags: FEATURE_FLAGS_PROPTYPES.isRequired,
    };

    static defaultProps = {
        id: 'global-header',
        onSearchSelect: setWindowLocation,
    };

    constructor(props) {
        super(props);

        this.state = {
            searchQuery: props.env.searchQuery || '',
            searchSuggestions: FALLBACK_SUGGESTIONS,
            showSearch: false,
        };
    }

    _handleCancelClick = () => {
        this.setState({
            showSearch: false,
        });
    };

    _handleSearchClick = () => {
        this.setState({
            showSearch: true,
        });
    };

    _handleOnAdditionalMenuTextItemSelect = (value, index) => {
        if (this.props.onAdditionalMenuTextItemSelect) {
            this.props.onAdditionalMenuTextItemSelect(value, index);
        }
    };

    _showCreateEventCTA = (
        { isAuthenticated, canCreateEvents },
        requestPath,
        launchNewNavigationHeader,
    ) => {
        // Only show the Create Event Cta if the user can create events, or is on the homepage
        const isHomePage = requestPath === '/';
        let showCreateEventCTA = canCreateEvents || isHomePage;
        if (launchNewNavigationHeader) {
            // if the launchNewNavigationHeader feature flag is on, show the CTA if the user is authenticated and it's
            // the homepage, otherwise, show if the user can create events
            showCreateEventCTA = isHomePage ? isAuthenticated : canCreateEvents;
        }
        return showCreateEventCTA;
    };

    _getHeaderLinks = () => {
        const {
            env: {
                ebDomain,
                helpUrls,
                signinUrl,
                signupUrl,
                loginUrl,
                organizeUrls,
                serverUrl,
                launchNewNavigationHeader,
                launchNewSignInSignUpLinks,
            },
            request: { path: requestPath },
            user,
            featureFlags,
        } = this.props;

        const { isAuthenticated, publicId } = user;

        const likesLinks = getLikesQuickLinks(
            serverUrl,
            ebDomain,
            requestPath,
            publicId,
        );
        const myTicketsLinks = getMyTicketsQuickLinks(
            serverUrl,
            ebDomain,
            requestPath,
            publicId,
        );

        const showCreateEventCTA = this._showCreateEventCTA(
            user,
            requestPath,
            launchNewNavigationHeader,
        );
        const callToActionInfo = getCreateEventCTA(
            serverUrl,
            ebDomain,
            requestPath,
            isAuthenticated,
            { showCreateEventCTA, launchNewNavigationHeader },
        );

        const additionalMenus = [
            getOrganizeMenu(
                organizeUrls,
                requestPath,
                launchNewNavigationHeader,
            ),
            getHelpMenu(helpUrls, requestPath, launchNewNavigationHeader),
        ];
        const ctaIcon = <Icon size="small" type={<PlusChunkySvg />} />;

        let linkComponents = [];

        // on the search page, show britelings a link to report a bug
        if (featureFlags.showReportBugLinkInHeader) {
            linkComponents = [
                <GlobalHeaderQuickLinks
                    key="bugLinks"
                    links={getBugQuickLinks()}
                    shouldShowIcon={true}
                />,
            ];
        }

        linkComponents = [
            ...linkComponents,

            <GlobalHeaderCallToAction
                key="createEventLink"
                linkInfo={callToActionInfo}
                iconType={ctaIcon}
                shouldShowIcon={true}
                iconColor="grey-800"
            />,
            <GlobalHeaderQuickLinks
                key="likesLinks"
                links={likesLinks}
                shouldShowIcon={true}
            />,
            <GlobalHeaderQuickLinks
                key="myTicketsLinks"
                links={myTicketsLinks}
                shouldShowIcon={true}
            />,
        ];

        if (!isAuthenticated) {
            const signinPath = launchNewSignInSignUpLinks
                ? signinUrl
                : loginUrl;
            const nonUserQuickLinks = getNonUserQuickLinks(
                requestPath,
                isAuthenticated,
                signinPath,
                signupUrl,
                { launchNewNavigationHeader, launchNewSignInSignUpLinks },
            );

            linkComponents = [
                <GlobalHeaderMenus
                    key="additionalMenus"
                    additionalMenus={additionalMenus}
                    onAdditionalMenuTextItemSelect={
                        this._handleOnAdditionalMenuTextItemSelect
                    }
                />,
                <GlobalHeaderCallToAction
                    key="createEventLinkNotAuth"
                    linkInfo={callToActionInfo}
                    shouldShowIcon={false}
                />,
                <GlobalHeaderQuickLinks
                    key="nonUserQuickLinks"
                    links={nonUserQuickLinks}
                    shouldShowIcon={false}
                />,
            ];
        }

        return <div className="consumer-header__links">{linkComponents}</div>;
    };

    _getGlobalHeaderSearch = (searchInfo) => {
        const { id, isSearchPage, onSearchSelect, handleSearch } = this.props;

        if (!isSearchPage) {
            return (
                <GlobalHeaderSearch
                    id={`${id}-desktop-search`}
                    searchInfo={searchInfo}
                    onSubmit={handleSearch}
                    onSelect={onSearchSelect}
                />
            );
        }

        return <div className="consumer-header__search__placeholder" />;
    };

    _getMobileHeaderLinks = () => {
        const {
            user,
            env: {
                adminServerUrl,
                ebDomain,
                loginUrl,
                logoutUrl,
                signinUrl,
                signupUrl,
                helpUrls,
                organizeUrls,
                isManageOverviewActive,
                serverUrl,
                launchNewNavigationHeader,
                launchNewSignInSignUpLinks,
            },
            request: { path: requestPath },
            featureFlags,
        } = this.props;

        const { isAuthenticated } = user;
        const additionalMenus = [
            getOrganizeMenu(
                organizeUrls,
                requestPath,
                launchNewNavigationHeader,
            ),
            getHelpMenu(helpUrls, requestPath, launchNewNavigationHeader),
        ];
        const userMenuInfo = getUserMenuInfo({
            adminServerUrl,
            featureFlags,
            isManageOverviewActive,
            logoutUrl,
            requestPath,
            serverUrl,
            showBrowseEvents: true,
            user,
        });

        const signinPath = launchNewSignInSignUpLinks ? signinUrl : loginUrl;
        const nonUserQuickLinks = getNonUserQuickLinks(
            requestPath,
            isAuthenticated,
            signinPath,
            signupUrl,
            { launchNewNavigationHeader, launchNewSignInSignUpLinks },
        );

        const showCreateEventCTA = this._showCreateEventCTA(
            user,
            requestPath,
            launchNewNavigationHeader,
        );
        const callToActionInfo = getCreateEventCTA(
            serverUrl,
            ebDomain,
            requestPath,
            isAuthenticated,
            { showCreateEventCTA, launchNewNavigationHeader },
        );

        // The new navigation header is not affecting logged in pages so only show the new
        // mobile header when the user is logged out.
        const narrowMenuInfo =
            launchNewNavigationHeader && !isAuthenticated
                ? getNarrowMenuLinks({
                      serverUrl,
                      requestPath,
                  })
                : getNarrowMenuInfo(
                      userMenuInfo,
                      additionalMenus,
                      [],
                      callToActionInfo,
                      [],
                      true,
                  );

        let narrowMenu;

        if (narrowMenuInfo) {
            const className = classNames(
                'eds-global-header__narrow-menu',
                'eds-show-down-mn',
            );

            narrowMenu = [narrowMenuInfo].map((menuInfo) => (
                <span key={menuInfo.label} className={className}>
                    <GlobalHeaderMenu
                        menuInfo={menuInfo}
                        data-spec="global-header-narrow-menu"
                    />
                </span>
            ));
        }

        return (
            <div className="consumer-header__links">
                {narrowMenu}
                <GlobalHeaderQuickLinks
                    links={nonUserQuickLinks}
                    shouldShowIcon={false}
                />
            </div>
        );
    };

    render() {
        const {
            id,
            env,
            user,
            request,
            isHomePage,
            isSearchPage,
            onSearchSelect,
            handleSearch,
            featureFlags,
        } = this.props;
        const { searchQuery, searchSuggestions, showSearch } = this.state;
        const { serverUrl, adminServerUrl, logoutUrl } = env;
        const { path: requestPath } = request;
        const logoInfo = getLogoInfo(serverUrl, requestPath, {
            iconWidth: '110px',
        });
        const searchInfo = getSearchInfo(
            serverUrl,
            searchQuery,
            searchSuggestions,
        );
        const userMenuInfo = getUserMenuInfo({
            adminServerUrl,
            featureFlags,
            logoutUrl,
            requestPath,
            serverUrl,
            showBrowseEvents: true,
            user,
        });

        const searchMobileClasses = classNames(
            'consumer-header__mobile-search',
            {
                'eds-hide': !showSearch,
                'eds-fx--fade-move-in': showSearch,
            },
        );

        return (
            <header
                id={id}
                className={`consumer-header ${
                    isHomePage ? '' : 'consumer-header__not-homepage'
                }`}
                data-spec="consumer-header"
            >
                <a
                    className="consumer-header__skip-links eds-is-hidden-accessible"
                    href="#skip-heading"
                >
                    {gettext('Skip Main Navigation')}
                </a>
                <div className="consumer-header__main" data-role="header">
                    {/* Switching to eds-show-up/down for now to fix the header CSS issues on mobile */}
                    {/* <MediaQuery breakpoint="isMediumUp" forceInitialRender={!isMobile}> */}
                    <div
                        className="consumer-header__content consumer-header__desktop eds-show-up-md"
                        data-spec="consumer-header-desktop"
                    >
                        <GlobalHeaderLogo info={logoInfo} />
                        {this._getGlobalHeaderSearch(searchInfo)}
                        {this._getHeaderLinks()}
                        <GlobalHeaderMenus userMenuInfo={userMenuInfo} />
                    </div>
                    {/* </MediaQuery> */}

                    {/* Switching to eds-show-up/down for now to fix the header CSS issues on mobile */}
                    {/* <MediaQuery breakpoint="isMediumDown" forceInitialRender={isMobile}> */}
                    <div
                        className="consumer-header__content consumer-header__mobile eds-show-down-mn eds-g-cell eds-g-cell--has-overflow eds-g-cell-1-1"
                        data-spec="consumer-header-mobile"
                    >
                        <div className="consumer-header__mobile-links eds-align--space-between">
                            <GlobalHeaderLogo info={logoInfo} />
                            <div className="eds-align--center-vertical">
                                {!isSearchPage && (
                                    <div className="consumer-header__mobile-search-button">
                                        <IconButton
                                            iconType={
                                                <MagnifyingGlassChunkySvg />
                                            }
                                            title={gettext('Search button')}
                                            iconColor="grey-600"
                                            onClick={this._handleSearchClick}
                                        />
                                    </div>
                                )}
                                {this._getMobileHeaderLinks()}
                            </div>
                        </div>
                        <div className={searchMobileClasses}>
                            <GlobalHeaderSearch
                                id={`${id}-mobile-search`}
                                searchInfo={searchInfo}
                                onSubmit={handleSearch}
                                onSelect={onSearchSelect}
                            />
                            <button
                                className="consumer-header__cancel-link eds-btn eds-btn--button eds-btn--link"
                                onClick={this._handleCancelClick}
                                type="button"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                    {/* </MediaQuery> */}
                </div>

                <span id="skip-heading" className="eds-is-hidden-accessible">
                    {gettext('Page Content')}
                </span>
            </header>
        );
    }
}

export default ConsumerHeader;
